import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState, useMemo } from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import TaskHeader from '../../components/TaskOverview/TaskHeader';
import NavButton from '../../components/NavButton';
import TrackTask from '../../components/TaskOverview/TrackTask';
import Comments from '../../components/Comments/Comments';
import Activities from '../../components/Activity/Activities';
import { useAccount } from '@azure/msal-react';
import AttachmentUpload from '../../components/AttachmentUpload/AttachmentUpload';
import { TaskExtended } from '../../../shared/types/Task';
import { CommentExtended } from '../../../shared/types/Comment';
import { useUpdateTaskMutation } from '../../services/tasksSlice/tasksSlice';
import { useGetCommentsQuery } from '../../services/commentSlice/commentSlice';
import { useGetAttachmentByIDQuery } from '../../services/attachmentSlice/attachmentSlice';
import { AttachmentExtended } from '../../../shared/types/Attachment';
import { useGetDataFieldsBySEPIDQuery } from '../../services/dataFieldsSlice';
import { DataFieldWithOptionsAndLocations } from '../../../shared/types/DataField';
import { env } from '../../../shared/env';
import { useLocation } from 'react-router-dom';
import { useGetSepQuery } from '../../services/sepSlice';
import { SEPPhase, SEPStatus } from '../../../shared/types/SEP';

export interface AttachmentState {
  sepID: number;
  taskID: number;
}

const NavLinks = ['Track Task', 'Attachments', 'Activity'];
interface TaskOverviewProps {
  task: TaskExtended;
}
const TaskOverview = ({ task }: TaskOverviewProps) => {
  const location = useLocation();
  const activeTab = location.state?.activeTab || 'Track Task';
  //console.log('Active Tab:', activeTab);
  const [curNav, setCurNav] = useState<string>(activeTab);
  const [updateTaskMutation] = useUpdateTaskMutation();
  const handleNavSelected = (link: string) => {
    setCurNav(link);
  };
  const { data: comments } = useGetCommentsQuery(task.sepID);
  const { data: attachments } = useGetAttachmentByIDQuery(task.sepID);
  const { data: dataFields, refetch } = useGetDataFieldsBySEPIDQuery(task.sepID);
  const { data: sep } = useGetSepQuery(task.sepID);

  const account = useAccount();
  const isAdminUser = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || (account?.idTokenClaims?.roles?.includes('AuthSuperUser') ?? false);
  const isComplete = sep?.phase === SEPPhase.complete;
  const disableAttachmentDelete = sep && [
    SEPStatus.approved,
    SEPStatus.cancelled,
    SEPStatus.rejected,
    SEPStatus.expired,
  ].includes(sep.status);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const roleList: any = useMemo(() => {
    if (env.REACT_APP_LOCAL_DEV_ADMIN === 'true') {
      return ['AuthSuperUser'];
    } else {
      return account?.idTokenClaims?.roles || [];
    }
  }, [account]);

  const taskComments = React.useMemo(() => {
    return (
      comments?.filter(
        (comment: CommentExtended) => comment.taskID === task.id
      ) || []
    );
  }, [task, comments]);

  const taskAttachments = React.useMemo(() => {
    return (
      attachments?.filter(
        (attachments: AttachmentExtended) => attachments.taskID === task.id
      ) || []
    );
  }, [task, attachments]);

  const taskDatafields = React.useMemo(() => {
   
    const filteredTaskDataFields =  dataFields?.filter((datafield: DataFieldWithOptionsAndLocations) => {
          return datafield.dataFieldLocations.find(
            (location) =>
              location.locationID === task.id && location.locationType === 'Task'
          );
        }) || []
    const mapOnlyTaskDataFieldLocations = filteredTaskDataFields.map((dataField)=>{
      const taskLocation = dataField.dataFieldLocations.find(
        (location) =>
          location.locationID === task.id && location.locationType === 'Task'
      );
      return {...dataField,taskDataFieldLocation : taskLocation} 
    })
    return mapOnlyTaskDataFieldLocations || []
  }, [task, dataFields]);

  const handleChange = (changed: TaskExtended) => {
    // Detect what changed
    let changeType = 'default';
    let assigneeName = '';
    let ownerName = '';
    let oldOwnerName = '';
    
    if (changed.assignedUserID !== task.assignedUserID) {
      changeType = 'assignee';
      assigneeName = changed.assignee?.displayName || changed.assignee?.givenName || 'Updated';
    } else if (changed.defaultReviewerID !== task.defaultReviewerID) {
      changeType = 'owner';
      oldOwnerName = task.defaultReviewer?.displayName || task.defaultReviewer?.givenName || 'Previous Owner';
      ownerName = changed.defaultReviewer?.displayName || changed.defaultReviewer?.givenName || 'Updated';
    } else if (changed.phase !== task.phase) {
      changeType = 'state';
    } else if (changed.name !== task.name) {
      changeType = 'name';
    }

    updateTaskMutation({
      taskID: task.id,
      task: {
        enabled: true,
        name: changed.name,
        shortName: changed.shortName,
        assignedUserID: changed.assignedUserID,
        defaultReviewerID: changed.defaultReviewerID,
        phase: changed.phase,
      } as any,
      changeType: changeType as any,
      taskName: changed.name as any,
      assigneeName: assigneeName as any,
      ownerName: ownerName as any,
      oldOwnerName: oldOwnerName as any,
    } as any);
  };

  return (
    <Box>
      <TaskHeader
        task={task}
        onChange={(changedTask) => handleChange(changedTask)}
      />
      <Divider />
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'baseline' }} spacing={2}>
          {NavLinks.map((link) => (
            <NavButton
              key={link}
              font="normal normal 600 18px/24px Open Sans;"
              onClick={() => handleNavSelected(link)}
              sx={
                link === curNav
                  ? {
                      color: '#0971f1',
                      backgroundColor: '#f3f8fb',
                    }
                  : {}
              }
            >
              {link}
            </NavButton>
          ))}
        </Stack>
        <Box sx={{ p: { xs: 0, sm: 1, md: 3 } }}>
          {curNav === 'Track Task' && (
            <>
              <TrackTask
                task={task}
                setCurNav={setCurNav}
                dataFields={taskDatafields}
                refetchData={refetch}
              />
              <Typography
                fontSize={typographyTokens.fontSizes.xl}
                fontWeight={typographyTokens.fontWeights.semibold}
                color={isDarkMode ? theme.palette.white.main :"gray.main"}
                mt="32px"
              >
                Task Comments
              </Typography>
              <Divider sx={{ mb: '32px' }} />
              <Comments
                taskID={task.id}
                sepID={task.sepID}
                comments={taskComments}
                roleList={roleList}
                showAddNewCommentButton = {true}
                taskInfo={task}
              />
            </>
          )}
          {curNav === 'Attachments' && taskAttachments && (
            <AttachmentUpload
              attachments={taskAttachments}
              sepID={task.sepID}
              taskID={task.id}
              isAdminUser={isAdminUser} 
              isComplete={isComplete}
              disableDelete={disableAttachmentDelete}
            />
          )}
          {curNav === 'Activity' && <Activities taskID={task.id} />}
        </Box>
      </Box>
    </Box>
  );
};

export default TaskOverview;
